import { formDataFetcher, postFetcher, putFetcher } from 'api/swr'
import type { PaintingRequest } from './types'

export const PAGE_SIZE = 60

export const listKey = (params: { limit: number; after?: string | null }) => {
  const q: Record<string, string | number> = { limit: params.limit }
  if (params.after) q.after = params.after
  return ['/painting-requests', q] as const
}

export const detailKey = (id: string) => [`/painting-requests/${id}`] as const

const postJson = async <T = unknown>(path: string, body: unknown): Promise<T> =>
  postFetcher(path, { arg: body }) as Promise<T>

const putJson = async <T = unknown>(path: string, body: unknown): Promise<T> =>
  putFetcher(path, { arg: body }) as Promise<T>

export const uploadImage = async (file: File): Promise<string> => {
  const formData = new FormData()
  formData.append('file', file)
  const res = (await formDataFetcher(`/painting-requests/upload-reference`, {
    arg: formData,
  })) as { url?: string }
  if (!res?.url) throw new Error('upload failed: no url returned')
  return res.url
}

export const uploadImages = async (files: File[]): Promise<string[]> => {
  const urls: string[] = []
  for (const f of files) {
    urls.push(await uploadImage(f))
  }
  return urls
}

export type UpsertBody = {
  styleDescription: string
  styleReferenceImages: string[]
  desc?: string
}

export type SyncFromBitableDraft = {
  recordId: string
  weekNo: string
  styleDescription: string
  styleReferenceImages: string[]
  desc: string
}

export type SyncFromBitablePreviewItem = {
  recordId: string
  weekNo: string
  styleDescription: string
  detailImageCount: number
  refImageCount: number
}

export const Api = {
  create: (body: UpsertBody) =>
    postJson<{ id: string }>(`/painting-requests`, body),
  update: (id: string, body: Partial<UpsertBody>) =>
    putJson<{ ok: true }>(`/painting-requests/${id}`, body),
  softDelete: (id: string) =>
    postJson<{ ok: true }>(`/painting-requests/${id}/delete`, {}),
  sendApproval: (id: string) =>
    postJson<{ instanceCode: string }>(
      `/painting-requests/${id}/send-approval`,
      {},
    ),
  previewFromBitable: (week: string) =>
    postJson<{ count: number; items: SyncFromBitablePreviewItem[] }>(
      `/painting-requests/preview-from-bitable`,
      { week },
    ),
  syncFromBitable: (week: string) =>
    postJson<{ items: SyncFromBitableDraft[] }>(
      `/painting-requests/sync-from-bitable`,
      { week },
    ),
  importFromBitable: (week: string) =>
    postJson<{ created: number; ids: string[]; errors: string[] }>(
      `/painting-requests/import-from-bitable`,
      { week },
    ),
}

export type { PaintingRequest }
